import React from 'react';
import { Box, Grid, makeStyles } from '@material-ui/core';
import { AccordionSummary } from '@material-ui/core';
import { Info } from 'react-feather';
import { injectIntl, IntlShape } from 'react-intl';
import { get } from 'lodash';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  formStyle,
  StyledTooltip,
  StyledCaption,
  StyledShortBody,
  StyledValidAccordion,
  StyledAccordionDetails,
  ColoredDivider,
  StyledGridBottom,
  StyledInlineGrid,
} from 'components/AccordionReleveSituation/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

import messages from 'components/AccordionReleveSituation/messages';
import { translate } from 'helpers/intl';
import { renderUnit, currencyFormat, renderUnitTaux } from 'utils/string';
import { useSelector } from 'react-redux';
import { isReverse } from 'helpers/intl';
import { getLocaleSelector } from 'store/LanguageProvider/selectors';
import FeatureToggleHOC from 'features/FeatureToggleHOC';
import { FEATURES } from 'features/features';

const useStyles = makeStyles({
  ...formStyle,
});

interface AccordionReleveSituationType {
  intl: IntlShape;
  epargnConstitue: number;
  epargnNette: number;
  tauxPreleveEpargn: number;
  fraisCapital: number;
  capitalSansParticipation: number;
  PartiBenificiare: number;
  epargnNettePB: number;
  expanded: boolean | String;
  handleChange: Function;
  handleOpenTooltipDialog: Function;
}

const AccordionReleveSituation = ({
  epargnConstitue,
  epargnNette,
  handleChange,
  tauxPreleveEpargn,
  fraisCapital,
  PartiBenificiare,
  capitalSansParticipation,
  epargnNettePB,
  expanded,
  handleOpenTooltipDialog,
  ...props
}: AccordionReleveSituationType) => {
  const lang = useSelector(getLocaleSelector);
  const reverse = isReverse(lang);
  const classes = useStyles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));

  return (
    <>
      <StyledValidAccordion
        aria-controls={`panel-content-1`}
        id={`panel-header-1`}
        data-testid={`releve-situation-item-1`}
        onChange={handleChange(`releve-situation-item-1`)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Box px={1} py={1} width="100%">
            <Grid container>
              <Grid container xs={12} sm={6} spacing={1}>
                <StyledInlineGrid item xs={12}>
                  <StyledCaption>
                    {translate(get(messages, 'epargnContitueBrute'), props)}
                  </StyledCaption>
                  <StyledTooltip
                    title={
                      matches
                        ? ''
                        : translate(
                            get(messages, 'epargnContitueBruteDescription'),
                            props,
                          )
                    }
                    onClick={handleOpenTooltipDialog(
                      translate(get(messages, 'epargnContitueBrute'), props),
                      translate(
                        get(messages, 'epargnContitueBruteDescription'),
                        props,
                      ),
                    )}
                  >
                    <Info className={classes.infoIcon} fontSize={'small'} />
                  </StyledTooltip>
                </StyledInlineGrid>
                <Grid item xs={12} dir="ltr">
                  <Box marginBottom={0}>
                    <StyledShortBody reverse={reverse}>
                      {currencyFormat(epargnConstitue) +
                        '' +
                        renderUnit(epargnConstitue, reverse)}
                    </StyledShortBody>
                  </Box>
                </Grid>
              </Grid>
              <Grid container xs={12} sm={6} spacing={1}>
                <StyledInlineGrid item xs={12}>
                  <StyledCaption>
                    {translate(get(messages, 'epargnNette'), props)}
                  </StyledCaption>
                  <StyledTooltip
                    title={
                      matches
                        ? ''
                        : translate(
                            get(messages, 'epargnNetteDescription'),
                            props,
                          )
                    }
                    onClick={handleOpenTooltipDialog(
                      translate(get(messages, 'epargnNette'), props),
                      translate(get(messages, 'epargnNetteDescription'), props),
                    )}
                  >
                    <Info className={classes.infoIcon} fontSize={'small'} />
                  </StyledTooltip>
                </StyledInlineGrid>
                <Grid item xs={12} dir="ltr">
                  <StyledShortBody reverse={reverse}>
                    {currencyFormat(epargnNette) +
                      '' +
                      renderUnit(epargnNette, reverse)}
                  </StyledShortBody>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </AccordionSummary>

        <StyledAccordionDetails>
          <Box py={3} px={[2, 3]}>
            <Grid container spacing={1}>
              <Grid container xs={12} sm={4}>
                <StyledGridBottom item xs={12}>
                  <StyledCaption>
                    {translate(get(messages, 'epargnContitueBrute'), props)}
                  </StyledCaption>
                </StyledGridBottom>

                <Grid item dir="ltr" xs={12}>
                  <Box marginBottom={0}>
                    <StyledShortBody reverse={reverse}>
                      {currencyFormat(epargnConstitue) +
                        '' +
                        renderUnit(epargnConstitue, reverse)}
                    </StyledShortBody>
                  </Box>
                </Grid>
              </Grid>

              {matches && (
                <Grid item xs={12} sm={4}>
                  <ColoredDivider />
                </Grid>
              )}

              <FeatureToggleHOC feature={FEATURES.NO_ACAPS}>
                <>
                  <Grid container xs={12} sm={4}>
                    <StyledGridBottom item xs={12}>
                      <StyledCaption>
                        {translate(get(messages, 'taux_preleve'), props)}
                      </StyledCaption>
                    </StyledGridBottom>
                    <Grid item dir="ltr" xs={12}>
                      <StyledShortBody reverse={reverse}>
                        {tauxPreleveEpargn +
                          '' +
                          renderUnitTaux(tauxPreleveEpargn)}
                      </StyledShortBody>
                    </Grid>
                  </Grid>

                  {matches && (
                    <Grid item xs={12} sm={4}>
                      <ColoredDivider />
                    </Grid>
                  )}
                </>
              </FeatureToggleHOC>

              <Grid container xs={12} sm={4}>
                <StyledInlineGrid item xs={12}>
                  <StyledCaption>
                    {translate(get(messages, 'montant_preleve'), props)}
                  </StyledCaption>
                  <StyledTooltip
                    title={
                      matches
                        ? ''
                        : translate(
                            get(messages, 'montant_preleve_description'),
                            props,
                          )
                    }
                    onClick={handleOpenTooltipDialog(
                      translate(get(messages, 'montant_preleve'), props),
                      translate(
                        get(messages, 'montant_preleve_description'),
                        props,
                      ),
                    )}
                  >
                    <Info className={classes.infoIcon} fontSize={'small'} />
                  </StyledTooltip>
                </StyledInlineGrid>

                <Grid item dir="ltr">
                  <StyledShortBody reverse={reverse}>
                    {currencyFormat(fraisCapital) +
                      '' +
                      renderUnit(fraisCapital, reverse)}
                  </StyledShortBody>
                </Grid>
              </Grid>

              {matches && (
                <Grid item xs={12} sm={4}>
                  <ColoredDivider />
                </Grid>
              )}

              <FeatureToggleHOC feature={FEATURES.NO_ACAPS}>
                <>
                  {!matches && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <ColoredDivider />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <ColoredDivider />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <ColoredDivider />
                      </Grid>
                    </>
                  )}
                </>
              </FeatureToggleHOC>

              <Grid container xs={12} sm={4}>
                <StyledInlineGrid item xs={12}>
                  <StyledCaption>
                    {translate(
                      get(messages, 'montantSansParticipation'),
                      props,
                    )}
                    <StyledTooltip
                      title={
                        matches
                          ? ''
                          : translate(
                              get(messages, 'epargnNetteDescription'),
                              props,
                            )
                      }
                      onClick={handleOpenTooltipDialog(
                        translate(
                          get(messages, 'montantSansParticipation'),
                          props,
                        ),
                        translate(
                          get(messages, 'montantSansParticipation'),
                          props,
                        ),
                      )}
                    >
                      <Info className={classes.infoIcon} fontSize={'small'} />
                    </StyledTooltip>
                  </StyledCaption>
                </StyledInlineGrid>
                <Grid item dir="ltr">
                  <Box marginBottom={0}>
                    <StyledShortBody reverse={reverse}>
                      {currencyFormat(capitalSansParticipation) +
                        '' +
                        renderUnit(capitalSansParticipation, reverse)}
                    </StyledShortBody>
                  </Box>
                </Grid>
              </Grid>

              <FeatureToggleHOC feature={FEATURES.ACAPS}>
                <>
                  {!matches && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <ColoredDivider />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <ColoredDivider />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <ColoredDivider />
                      </Grid>
                    </>
                  )}
                </>
              </FeatureToggleHOC>

              {matches && (
                <Grid item xs={12} sm={4}>
                  <ColoredDivider />
                </Grid>
              )}

              <Grid container xs={12} sm={4}>
                <StyledInlineGrid item xs={12}>
                  <StyledCaption>
                    {translate(
                      get(messages, 'participation_aux_benificaire'),
                      props,
                    )}
                  </StyledCaption>
                  <StyledTooltip
                    title={
                      matches
                        ? ''
                        : translate(
                            get(
                              messages,
                              'participation_aux_benificaire_description',
                            ),
                            props,
                          )
                    }
                    onClick={handleOpenTooltipDialog(
                      translate(
                        get(messages, 'participation_aux_benificaire'),
                        props,
                      ),
                      translate(
                        get(
                          messages,
                          'participation_aux_benificaire_description',
                        ),
                        props,
                      ),
                    )}
                  >
                    <Info className={classes.infoIcon} fontSize={'small'} />
                  </StyledTooltip>
                </StyledInlineGrid>

                <Grid item xs={12} dir="ltr">
                  <StyledShortBody reverse={reverse}>
                    {currencyFormat(PartiBenificiare) +
                      '' +
                      renderUnit(PartiBenificiare, reverse)}
                  </StyledShortBody>
                </Grid>
              </Grid>

              {matches && (
                <Grid item xs={12} sm={4}>
                  <ColoredDivider />
                </Grid>
              )}

              {/* <Grid container xs={12} sm={4}>
                <StyledInlineGrid item xs={12}>
                  <StyledCaption>
                    {translate(
                      get(messages, 'epargne_nette_avec_participation'),
                      props,
                    )}
                  </StyledCaption>
                  <StyledTooltip
                    title={
                      matches
                        ? ''
                        : translate(
                            get(messages, 'epargne_nette_avec_participation'),
                            props,
                          )
                    }
                    onClick={handleOpenTooltipDialog(
                      translate(
                        get(messages, 'epargne_nette_avec_participation'),
                        props,
                      ),
                      translate(
                        get(messages, 'epargne_nette_avec_participation'),
                        props,
                      ),
                    )}
                  >
                    <Info className={classes.infoIcon} fontSize={'small'} />
                  </StyledTooltip>
                </StyledInlineGrid>
                <Grid item dir="ltr">
                  <StyledShortBody reverse={reverse}>
                    {currencyFormat(epargnNettePB) +
                      '' +
                      renderUnit(epargnNettePB, reverse)}
                  </StyledShortBody>
                </Grid>
              </Grid> */}
            </Grid>
          </Box>
        </StyledAccordionDetails>
      </StyledValidAccordion>
    </>
  );
};

export default injectIntl(AccordionReleveSituation);
